// ========================================
// useResentments Hook
// ========================================
// Custom hook for managing resentment list

import { useState, useEffect } from 'react';
import { useAuth } from '../components/Auth/AuthContext';
import {
  getResentments,
  createResentment,
  updateResentment,
  deleteResentment,
} from '../firebase/firestore';
import { Resentment } from '../types';

export const useResentments = () => {
  const { currentUser } = useAuth();
  const [resentments, setResentments] = useState<Resentment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load resentments
  useEffect(() => {
    if (!currentUser) {
      setResentments([]);
      setLoading(false);
      return;
    }

    const loadResentments = async () => {
      try {
        setLoading(true);
        const data = await getResentments(currentUser.uid);
        setResentments(data);
        setError(null);
      } catch (err) {
        console.error('Error loading resentments:', err);
        setError('Failed to load resentments');
      } finally {
        setLoading(false);
      }
    };

    loadResentments();
  }, [currentUser]);

  // Add new resentment
  const addResentment = async (
    resentmentData: Omit<Resentment, 'id' | 'dateAdded' | 'status'>
  ) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await createResentment(currentUser.uid, resentmentData);

      // Refresh resentments list
      const updatedResentments = await getResentments(currentUser.uid);
      setResentments(updatedResentments);
    } catch (err) {
      console.error('Error adding resentment:', err);
      throw err;
    }
  };

  // Update existing resentment
  const editResentment = async (id: string, updates: Partial<Resentment>) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await updateResentment(currentUser.uid, id, updates);

      // Refresh resentments list
      const updatedResentments = await getResentments(currentUser.uid);
      setResentments(updatedResentments);
    } catch (err) {
      console.error('Error updating resentment:', err);
      throw err;
    }
  };

  // Delete resentment
  const removeResentment = async (id: string) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      await deleteResentment(currentUser.uid, id);

      // Refresh resentments list
      const updatedResentments = await getResentments(currentUser.uid);
      setResentments(updatedResentments);
    } catch (err) {
      console.error('Error deleting resentment:', err);
      throw err;
    }
  };

  return {
    resentments,
    loading,
    error,
    addResentment,
    editResentment,
    removeResentment,
  };
};
